import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderNothing, withProps } from 'recompose';
import { Button, ButtonToolbar, Clearfix, Glyphicon, Nav, NavItem } from 'react-bootstrap';
import _ from 'lodash';

import TicketReview from './TicketReview';
import { clearErrorMessage, selectTicketId, sendJobToSage, setErrorMessage } from '../actions';
import queryMetaHandler from '../../../utils/queryMetaHandler';
import { openInNewTab, querySelector } from '../../../utils';
import omitProps from '../../../utils/omitProps';
import linkWithCredentials from '../../../utils/linkWithCredentials';
import { getApiServerUrl } from '../../configuration/selectors';
import ticketsOnJob from '../graphql/ticketsOnJob';

import { getErrorMessage, getSelectedTicketId } from '../selectors';

import SageInfoModal, {
  sageInfoModalPropTypes,
  withSageInfoModalState,
} from './SageInfoModal';

import ConfirmModal, {
  withConfirmModalState,
  confirmModalPropTypes,
} from '../../confirmModal/confirmModal';

import { dispatchUpdateLease } from '../../admin/leaseList/actions';
import { getTicketLink } from '../../ticketViewer/links';
import Overlay from '../../../components/Overlay/index';
import { tryHandleGraphQLError } from '../../../utils/graphQLErrorHandlers';
import { downloadingFileAndUpdatingCredentials } from '../../../utils/downloadUpdatingCredentials';

const nodeType = PropTypes.shape({
  id: PropTypes.number,
});


const propTypes = exact({
  jobId: PropTypes.number.isRequired,
  tickets: PropTypes.arrayOf(nodeType).isRequired,
  job: nodeType.isRequired,
  selectedTicketId: PropTypes.number.isRequired,
  selectTicketIdAction: PropTypes.func.isRequired,
  apiServerUrl: PropTypes.string.isRequired,

  createJobInvoiceAction: PropTypes.func.isRequired,
  updateLeaseAction: PropTypes.func.isRequired,

  errorMessage: PropTypes.string,
  setErrorMessageAction: PropTypes.func.isRequired,
  clearErrorMessageAction: PropTypes.func.isRequired,

  ...sageInfoModalPropTypes,
  ...confirmModalPropTypes,
});


const DeferredJobReview = (props) => {
  const { selectTicketIdAction } = props;

  const {
    createJobInvoiceAction,
    updateLeaseAction,
  } = props;

  const {
    showSageInfoModal,
    openSageInfoModal,
    closeSageInfoModal,
    selectedTicketId,
  } = props;

  const {
    showModal,
    closeModal,
    openModal,
    setErrorMessageAction,
    clearErrorMessageAction,
    errorMessage,
  } = props;

  const renderTab = (ticket) => {
    const { id, date } = ticket;
    const navDate = `${date.month}/${date.day}`;
    const handleClick = () => { selectTicketIdAction(id); };
    return (
      <NavItem key={id} eventKey={id} onClick={handleClick}>{navDate}</NavItem>
    );
  };

  const handlePromptSageInfo = async (currentJob) => {
    const { sage_info: sageInfo } = currentJob;

    if (sageInfo) {
      try {
        await createJobInvoiceAction(currentJob.id);
        openInNewTab(getTicketLink(selectedTicketId, currentJob.id, false));
      } catch (e) {
        const [message] = tryHandleGraphQLError(e);
        const eMessage = `${message}  -  Contact support and try again later.`;
        setErrorMessageAction(eMessage);
        openModal();
      }
    } else {
      openSageInfoModal();
    }
  };

  const {
    apiServerUrl,
    job,
    tickets,
  } = props;

  const { lease, sage_sendable: sageSendable } = job;

  const handlePdfDownload = () => {
    const pdfUrl = linkWithCredentials(`${apiServerUrl}${job.pdf_path}`);
    downloadingFileAndUpdatingCredentials(pdfUrl);
  };

  return (
    <div>
      <Nav bsStyle="tabs" activeKey={selectedTicketId}>
        {tickets.map(renderTab, this)}
      </Nav>
      <div className="panel panel-default panel-tabbed">
        <TicketReview ticketId={selectedTicketId} />
        <div className="panel-footer">
          <Clearfix>
            <ButtonToolbar className="pull-left">
              <Button bsStyle="info" onClick={handlePdfDownload}>
                <Glyphicon glyph="export" style={{ marginRight: '0.5em' }} />
                Export Tickets as PDF
              </Button>
            </ButtonToolbar>
            <ButtonToolbar className="pull-right">
              <div className="inline-btn-styling">
                <Overlay enabled={!sageSendable} message="Already in Sage">
                  <div className="tooltip-area">
                    <Button
                      disabled={!sageSendable}
                      bsStyle="primary"
                      onClick={() => { return handlePromptSageInfo(job); }}
                    >
                      <Glyphicon glyph="envelope" style={{ marginRight: '0.5em' }} />
                      Send to Sage
                    </Button>
                  </div>
                </Overlay>
              </div>
            </ButtonToolbar>
          </Clearfix>
        </div>
      </div>
      <SageInfoModal
        job={job}
        lease={lease}
        ticketId={selectedTicketId}
        show={showSageInfoModal}
        onHide={closeSageInfoModal}
        onSubmit={updateLeaseAction}
        createJobInvoiceAction={createJobInvoiceAction}
      />
      <ConfirmModal
        title="Error Sending To Sage"
        message={errorMessage}
        confirmButtonText="OK"
        confirmAction={() => {
          clearErrorMessageAction();
          closeModal();
        }}
        showDismissButton={false}
        onHide={closeModal}
        show={showModal}
      />
    </div>
  );
};


DeferredJobReview.propTypes = propTypes;


const mapDataToProps = withProps((props) => {
  const { job } = props.data;
  const tickets = querySelector(job, '$.tickets.edges[*].node', []);
  return {
    tickets,
    job,
  };
});


const mapStateToProps = (state) => {
  return {
    jobId: state.ticketInvoicing.jobId,
    selectedTicketId: getSelectedTicketId(state),
    apiServerUrl: getApiServerUrl(state),
    errorMessage: getErrorMessage(state),
  };
};


const mapDispatchToProps = {
  selectTicketIdAction: selectTicketId,
  createJobInvoiceAction: sendJobToSage,
  updateLeaseAction: dispatchUpdateLease,
  setErrorMessageAction: setErrorMessage,
  clearErrorMessageAction: clearErrorMessage,
};


const withRedux = connect(mapStateToProps, mapDispatchToProps);


const withHide = branch(
  (props) => { return props.jobId === null; },
  renderNothing,
);


const withHideNoTicketId = branch(
  (props) => { return props.selectedTicketId === null; },
  renderNothing,
);


const withSelectFirstTicket = lifecycle({
  componentWillMount() {
    const { tickets, selectTicketIdAction } = this.props;
    if (tickets.length > 0) {
      selectTicketIdAction(tickets[0].id);
    }
  },
  componentWillReceiveProps(nextProps) {
    const { tickets, selectTicketIdAction } = this.props;
    const { tickets: nextTickets } = nextProps;
    if (_.isEqual(tickets, nextTickets)) {
      return;
    }
    if (nextTickets.length > 0) {
      selectTicketIdAction(nextTickets[0].id);
    }
  },
});


const enhance = compose(
  withRedux,
  withHide,
  ticketsOnJob,
  queryMetaHandler,
  mapDataToProps,
  omitProps(['data']),
  withSelectFirstTicket,
  withHideNoTicketId,
  withSageInfoModalState,
  withConfirmModalState,
);


export default enhance(DeferredJobReview);
